import { useForm, FieldData, Box, SwitchIndicator } from 'vopay';

import { ChangeEvent, useImperativeHandle, useMemo, useEffect, Ref, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type PermissionsMap, BodyText, TextField, MaterialSymbol, withStyles } from 'vopay';
import { Title, UtilButton } from 'UI';
import CollapsibleList from './CollapsibleList';
import styles from './AccountUserPermissionsForm.module.scss';

const classes = withStyles(styles);

type PermissionFormValues = {
  [key: string]: boolean;
};

type AccountUserPermissionsFormProps = {
  onAccountUserPermissionSave: (values: PermissionFormValues) => void;
  permissions: PermissionsMap;
  ref: Ref<AccountUserPermissionsFormRef>;
  onFormStateChange: (hasChanges: boolean, isValid: boolean) => void;
  onSearchChange: (isSearching: boolean) => void;
  isReadOnly?: boolean;
};

export type AccountUserPermissionsFormRef = {
  enableAll: () => void;
};

type ToggleProps = {
  name: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
};
const Toggle: FC<ToggleProps> = ({ name, checked, onChange, disabled = false }) => {
  return (
    <>
      <SwitchIndicator checked={checked} disabled={disabled} />
      <input
        className={classes('hiddenCheckbox')}
        id={`checkbox-${name}`}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
    </>
  );
};

function buildPermissionsConfig(
  permissions: PermissionsMap,
  isReadOnly: boolean,
): Record<string, FieldData<PermissionFormValues>[]> {
  const config: Record<string, FieldData<PermissionFormValues>[]> = {};

  Object.entries(permissions).forEach(([groupName, permissionGroup]) => {
    const groupFields: FieldData<PermissionFormValues>[] = [];

    Object.entries(permissionGroup).forEach(([permissionKey, permissionItem]) => {
      groupFields.push({
        fieldName: permissionKey,
        label: () => permissionItem.label,
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<boolean>({});
          return (
            <Toggle
              name={fieldData.name}
              checked={fieldData.value}
              onChange={fieldData.onChange}
              disabled={isReadOnly}
            />
          );
        },
      });
    });

    config[groupName] = groupFields;
  });

  return config;
}

function buildInitialValues(permissions: PermissionsMap): PermissionFormValues {
  const initialValues: PermissionFormValues = {};

  Object.values(permissions).forEach((permissionGroup) => {
    Object.entries(permissionGroup).forEach(([permissionKey, permissionItem]) => {
      initialValues[permissionKey] = permissionItem.permitted;
    });
  });

  return initialValues;
}

const AccountUserPermissionsForm: FC<AccountUserPermissionsFormProps> = ({
  onAccountUserPermissionSave,
  permissions,
  onFormStateChange,
  onSearchChange,
  ref,
  isReadOnly = false,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserPermissions.Form' });
  const [searchTerm, setSearchTerm] = useState('');

  const initialValues = buildInitialValues(permissions);
  const permissionsConfig = useMemo(() => buildPermissionsConfig(permissions, isReadOnly), [isReadOnly]);

  const { ctx, renderFormElements } = useForm<PermissionFormValues>({
    config: Object.values(permissionsConfig).flat(),
    initialValues,
    onSubmit: (values) => {
      onAccountUserPermissionSave(values);
    },
  });

  // Check if form has changes and is valid
  const hasChanges = useMemo(() => {
    return JSON.stringify(ctx.values) !== JSON.stringify(initialValues);
  }, [ctx.values, initialValues]);

  const hasAtLeastOnePermission = useMemo(() => {
    return Object.values(ctx.values).some((value) => value === true);
  }, [ctx.values]);

  // Notify parent of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(hasChanges, hasAtLeastOnePermission);
    }
  }, [hasChanges, hasAtLeastOnePermission]);

  // Notify parent when search state changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchTerm.trim().length > 0);
    }
  }, [searchTerm, onSearchChange]);

  // Track how many permissions are enabled per group
  const groupStats = useMemo(() => {
    const stats: Record<string, { enabled: number; total: number }> = {};

    Object.entries(permissionsConfig).forEach(([groupName, groupFields]) => {
      const total = groupFields.length;
      const enabled = groupFields.filter((field) => ctx.values[field.fieldName]).length;
      stats[groupName] = { enabled, total };
    });

    return stats;
  }, [permissionsConfig, ctx.values]);

  // Function to enable all permissions in a specific group
  const enableAllInGroup = (groupName: string) => {
    const groupFields = permissionsConfig[groupName];
    if (!groupFields) return;

    const updates: Record<string, boolean> = {};
    groupFields.forEach((field) => {
      updates[field.fieldName] = true;
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Function to disable all permissions in a specific group
  const disableAllInGroup = (groupName: string) => {
    const groupFields = permissionsConfig[groupName];
    if (!groupFields) return;

    const updates: Record<string, boolean> = {};
    groupFields.forEach((field) => {
      updates[field.fieldName] = false;
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Function to enable all permissions
  const enableAll = () => {
    const updates: Record<string, boolean> = {};
    Object.values(permissionsConfig)
      .flat()
      .forEach((field) => {
        updates[field.fieldName] = true;
      });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    enableAll,
  }));

  // Filter permissions based on search term
  const filteredPermissionsConfig = useMemo(() => {
    if (!searchTerm.trim()) {
      return permissionsConfig;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered: Record<string, FieldData<PermissionFormValues>[]> = {};

    Object.entries(permissionsConfig).forEach(([groupName, groupFields]) => {
      const groupMatches = groupName.toLowerCase().includes(lowerSearchTerm);

      // Filter permissions within the group by checking their labels
      const matchingPermissions = groupFields.filter((field) => {
        const label = field.label?.(ctx) || '';
        return String(label).toLowerCase().includes(lowerSearchTerm);
      });

      if (groupMatches || matchingPermissions.length > 0) {
        filtered[groupName] = groupMatches ? groupFields : matchingPermissions;
      }
    });

    return filtered;
  }, [permissionsConfig, searchTerm]);

  const hasFilteredResults = Object.keys(filteredPermissionsConfig).length > 0;

  return (
    <form onSubmit={ctx.handleSubmit} id="accountUserPermissionsForm" className={classes('formContainer')}>
      <Box mb="md" base="8" flx="col" className={classes('searchBox')}>
        <TextField
          name="permissionSearch"
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlineShadow"
          elementLeft={
            <div className={classes('searchIconWrapper')}>
              <MaterialSymbol icon="search" size="24px" />
            </div>
          }
        />
      </Box>

      {!hasFilteredResults && searchTerm.trim() ? (
        <div className={classes('emptyStateWrapper')}>
          <Box flx="col" ai="center" jc="center" gap="sm">
            <MaterialSymbol icon="manage_search" size="90px" color="rgba(45, 45, 45, 0.2)" />
            <Title level="4">{t('permissionNotFound')}</Title>
          </Box>
        </div>
      ) : (
        <CollapsibleList>
          {Object.entries(filteredPermissionsConfig).map(([groupName, groupFields]) => {
            const stats = groupStats[groupName];
            const allEnabled = stats.enabled === stats.total;
            const isFiltering = searchTerm.trim().length > 0;

            return (
              <CollapsibleList.Item key={groupName} defaultOpen={false} open={isFiltering}>
                <CollapsibleList.Trigger>
                  <Box flx="row" jc="sb" ai="center" grow="1">
                    <Box ai="center" gapSize="12px">
                      <Title level="5">{groupName}</Title>
                      <BodyText weight="rg" className={classes('groupStatsText')}>
                        ({stats.enabled}/{stats.total})
                      </BodyText>
                    </Box>
                    {!isFiltering && !isReadOnly && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Box gap="sm">
                          {allEnabled ? (
                            <UtilButton size="medium" onClick={() => disableAllInGroup(groupName)}>
                              {t('disableAll')}
                            </UtilButton>
                          ) : (
                            <UtilButton size="medium" onClick={() => enableAllInGroup(groupName)}>
                              {t('enableAll')}
                            </UtilButton>
                          )}
                        </Box>
                      </div>
                    )}
                  </Box>
                </CollapsibleList.Trigger>

                <CollapsibleList.Content>
                  <Box flx="col" gap="md" base="8">
                    {renderFormElements({
                      config: groupFields,
                      override: (fieldData) => {
                        return (
                          <label className={classes('permissionLabel')} htmlFor={`checkbox-${fieldData.fieldName}`}>
                            <BodyText className={classes('permissionLabelText')} weight="md">
                              {fieldData.label}
                            </BodyText>

                            {fieldData.template}
                          </label>
                        );
                      },
                    })}
                  </Box>
                </CollapsibleList.Content>
              </CollapsibleList.Item>
            );
          })}
        </CollapsibleList>
      )}
    </form>
  );
};

export default AccountUserPermissionsForm;
